import { AlertTriangle, Check, Info, Loader2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ENTITY_TYPE_LABELS } from "../types";
import type { ExtractedPrimaryEntity, ExtractedSubEntity, WizardState } from "../types";
import { ConfidenceDots } from "../ConfidenceDots";

interface Step4Props {
  state: WizardState;
  /** External-entity confirm step: extraction runs in the background (see Step1Extract /
   * openExtractionSocket), so this step can be reached before it's finished. */
  isExtracting?: boolean;
  /** Discovery review (backlog #18) — toggles ExtractedSubEntity.accepted at `index`. */
  onToggleCandidate: (index: number) => void;
  confirmError?: string | null;
}

export function Step4Review({ state, isExtracting, onToggleCandidate, confirmError }: Step4Props) {
  const review = state.extractedReview;
  const readOnly = state.candidatesConfirmed;

  if (isExtracting && !review) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center flex flex-col items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/70" />
        <p className="text-lg text-muted-foreground/70">
          Scanning the website…
        </p>
      </div>
    );
  }

  if (!review || !review.primary_entity) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <p className="text-lg text-muted-foreground/70">
          No extraction data found. Go back and run the extraction again.
        </p>
      </div>
    );
  }

  const { primary_entity, sub_entities = [], has_duplicate_warning } = review;
  const quickSetup = review.quick_setup === true;
  const people = state.detectedPersonEntities;

  return (
    <div className="max-w-2xl mx-auto px-6 py-10 flex flex-col gap-7">
      {has_duplicate_warning && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.05] px-4 py-3.5">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-lg text-amber-400/90 leading-snug">
            A similar entity already exists. Review carefully before confirming.
          </p>
        </div>
      )}

      <div>
        <p className="text-label font-semibold text-subtle-foreground uppercase tracking-widest mb-3">
          Primary entity
        </p>
        <PrimaryEntityCard entity={primary_entity} />
      </div>

      {quickSetup && (
        /* Sets the expectation the empty list below would otherwise leave open: nothing was
           missed, nothing was searched — and the full scan is one click away, later. */
        <div className="flex items-start gap-3 rounded-xl border border-border/40 bg-muted/10 px-4 py-3.5">
          <Info className="h-4 w-4 text-muted-foreground/70 shrink-0 mt-0.5" />
          <p className="text-md text-muted-foreground/80 leading-relaxed">
            <span className="text-foreground/90 font-medium">Quick setup</span> — Raisy read this
            entity itself. The other products, brands and people the page mentions were not
            searched for. You can run the full scan any time from the entity page.
          </p>
        </div>
      )}

      {people.length > 0 && (
        <div>
          <p className="text-label font-semibold text-subtle-foreground uppercase tracking-widest mb-3">
            People found — already added
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {people.map((p) => (
              <div
                key={p.entity_id}
                className="rounded-xl border border-border/50 bg-muted/10 p-4 flex items-center gap-2.5"
              >
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-600 shrink-0">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <p className="text-base font-semibold text-foreground leading-tight truncate">{p.entity_name}</p>
              </div>
            ))}
          </div>
          <p className="text-md text-subtle-foreground mt-2">
            People are saved as soon as they're found, so you can be matched against them — nothing to confirm here.
          </p>
        </div>
      )}

      {sub_entities.length > 0 && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-label font-semibold text-subtle-foreground uppercase tracking-widest">
              Found while scanning — pick which to keep
            </p>
            {!readOnly && (
              <span className="text-md text-muted-foreground/70 shrink-0">
                {sub_entities.filter((s) => s.accepted).length} of {sub_entities.length} selected
              </span>
            )}
          </div>
          {(() => {
            const indexed = sub_entities.map((entity, index) => ({ entity, index }));
            const internal = indexed.filter(({ entity }) => entity.is_part_of_parent !== false);
            const external = indexed.filter(({ entity }) => entity.is_part_of_parent === false);
            return (
              <>
                {internal.length > 0 && (
                  <div>
                    <p className="text-label font-semibold text-subtle-foreground uppercase tracking-widest mb-3">
                      Also found inside
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {internal.map(({ entity, index }) => (
                        <SubEntityCard
                          key={index}
                          entity={entity}
                          readOnly={readOnly}
                          onToggle={() => onToggleCandidate(index)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {external.length > 0 && (
                  <div>
                    <p className="text-label font-semibold text-subtle-foreground uppercase tracking-widest mb-3">
                      External affiliates
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {external.map(({ entity, index }) => (
                        <SubEntityCard
                          key={index}
                          entity={entity}
                          isExternal
                          readOnly={readOnly}
                          onToggle={() => onToggleCandidate(index)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          {confirmError && (
            <p className="text-md text-destructive">{confirmError}</p>
          )}
        </div>
      )}
    </div>
  );
}

function PrimaryEntityCard({ entity }: { entity: ExtractedPrimaryEntity }) {
  return (
    <div className="rounded-xl border border-border/50 bg-muted/10 p-5 flex flex-col gap-4">
      {entity.low_confidence && (
        <div className="flex items-center gap-2 text-md text-amber-400 bg-amber-500/8 border border-amber-500/20 rounded-lg px-3 py-2 w-fit">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          Low confidence — please review carefully
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-2xl font-bold text-foreground tracking-tight leading-tight">
            {entity.suggested_name}
          </h3>
          {entity.description && (
            <p className="text-lg text-muted-foreground/70 mt-2 line-clamp-3 leading-relaxed">
              {entity.description}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2.5 shrink-0">
          <Badge variant="outline" className="text-md font-medium border-border/50 text-muted-foreground">
            {ENTITY_TYPE_LABELS[entity.entity_type] ?? entity.entity_type}
          </Badge>
          <ConfidenceDots filled={entity.confidence_dots} />
        </div>
      </div>

      {(entity.industry || entity.target_market) && (
        <div className="flex flex-wrap gap-1.5 pt-3 border-t border-border/30">
          {entity.industry && (
            <span className="text-md rounded-full bg-muted/50 border border-border/40 px-2.5 py-0.5 text-muted-foreground/70">
              {entity.industry}
            </span>
          )}
          {entity.target_market && (
            <span className="text-md rounded-full bg-muted/50 border border-border/40 px-2.5 py-0.5 text-muted-foreground/70">
              {entity.target_market}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function SubEntityCard({
  entity,
  isExternal,
  readOnly,
  onToggle,
}: {
  entity: ExtractedSubEntity;
  isExternal?: boolean;
  readOnly?: boolean;
  onToggle: () => void;
}) {
  const accepted = entity.accepted;
  return (
    <div className={cn(
      "rounded-xl border p-4 flex flex-col gap-2.5 transition-opacity",
      isExternal
        ? "border-violet-500/20 bg-violet-500/[0.03]"
        : "border-border/50 bg-muted/10",
      !accepted && "opacity-45"
    )}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <button
            type="button"
            disabled={readOnly}
            onClick={onToggle}
            title={readOnly ? undefined : accepted ? "Reject — exclude from import" : "Accept — include in import"}
            className={cn(
              "flex items-center justify-center h-6 w-6 rounded-full shrink-0 transition-colors mt-0.5",
              readOnly && "cursor-default",
              accepted
                ? "bg-emerald-500/15 text-emerald-600" + (readOnly ? "" : " hover:bg-red-500/15 hover:text-red-500")
                : "bg-muted/40 text-muted-foreground" + (readOnly ? "" : " hover:bg-emerald-500/15 hover:text-emerald-600"),
            )}
          >
            {accepted ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
          </button>
          <p className="text-base font-semibold text-foreground leading-tight truncate">{entity.suggested_name}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge variant="outline" className="text-md border-border/50 text-muted-foreground">
            {ENTITY_TYPE_LABELS[entity.entity_type] ?? entity.entity_type}
          </Badge>
          {isExternal && (
            <Badge variant="outline" className="text-label border-violet-500/30 text-violet-400">
              {entity.role_title || entity.relationship_type.replace(/_/g, " ") || "External"}
            </Badge>
          )}
        </div>
      </div>
      {entity.description && (
        <p className="text-md text-muted-foreground/70 leading-relaxed line-clamp-2">{entity.description}</p>
      )}
      <ConfidenceDots filled={entity.confidence_dots} />
    </div>
  );
}
